import type { HydraCollection } from '../../../types';

import { useApiRead } from 'api-read-hook';
import { css, html } from 'react-strict-dom';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';

import { Api } from '../../../api';
import Tabs from '../../../components/Tabs';
import Text from '../../../components/Text';
import { useCompany } from '../../../contexts/ClientDataContext';
import { useMyUserClient } from '../../../hooks';

export type TicketsTab = 'tickets' | 'monthly' | 'weekly';

type Props = {
  companySlug: string;
  selected: TicketsTab;
  /**
   * Approvals the page has already loaded (the report pages fetch them for
   * their own period logic).  api-read-hook hooks are not deduplicated, so
   * passing the collection in here stops the tabs firing a duplicate GET.
   * Fetched internally when omitted.
   */
  approvals?: Api.TicketReportApproval.V_Client[];
};

/**
 * The page-level navigation above the ticket list and the summary screens.
 * Agency users always see the monthly and weekly summaries; a company user
 * only sees a summary tab once at least one period of that type has been
 * approved for them.
 */
export default function MaintenanceTicketsTabs({
  companySlug,
  selected,
  approvals,
}: Props) {
  const company = useCompany(companySlug);
  const myUser = useMyUserClient();
  const apiApprovals = useApiRead<
    HydraCollection<Api.TicketReportApproval.V_Client>
  >(
    approvals !== undefined
      ? null
      : `/companies/${company.id}/ticket-report-approvals`
  );
  const apiOpen = Api.Ticket.useReadCollection({
    company: company['@id'],
    'exists[closedAt]': false,
  });

  const isAgency = !!myUser.agencyRole;
  const approvalsList = approvals ?? apiApprovals.data?.['hydra:member'] ?? [];
  const hasMonthly = approvalsList.some((a) => a.month != null);
  const hasWeekly = approvalsList.some((a) => a.week != null);

  const tabs: Array<{
    key: string;
    label: string;
    to: string;
    count?: number;
    selected: boolean;
  }> = [
    {
      key: 'tickets',
      label: 'Tickets',
      to: `/company/${companySlug}/tickets`,
      count: apiOpen.data?.['hydra:totalItems'],
      selected: selected === 'tickets',
    },
  ];
  if (isAgency || hasMonthly) {
    tabs.push({
      key: 'monthly',
      label: 'Monthly Summary',
      to: `/company/${companySlug}/tickets/report/monthly`,
      selected: selected === 'monthly',
    });
  }
  if (isAgency || hasWeekly) {
    tabs.push({
      key: 'weekly',
      label: 'Weekly Summary',
      to: `/company/${companySlug}/tickets/report/weekly`,
      selected: selected === 'weekly',
    });
  }

  return (
    <html.div style={Style.col}>
      <Text typeface="headline" color="neutral100" as="h1">
        Maintenance Tickets
      </Text>
      <html.div style={styles.tabBar}>
        <Tabs accentColor={Color.accent} alwaysShowCount tabs={tabs} />
      </html.div>
    </html.div>
  );
}

const styles = css.create({
  tabBar: {
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    marginTop: 24,
  },
});
